import Axios, { VirtualDeviceApi } from '@/api';
import { DeviceType } from '@/common/constants';
import { supportsLampCmdCs } from '@/common/utils/controllerDisplayName';

/** mqtt-server / front 가 개별 조명 조작으로 오인해 가상버튼을 끄지 않도록 표시 */
export const VIRTUAL_BUTTON_MQTT_ORIGIN = { requester: 'virtualButton' };

const normalizeItems = (items) => {
  if (items == null) return [];
  if (Array.isArray(items)) return items;
  if (typeof items === 'object') return Object.values(items);
  return [];
};

const lampPortsOf = (it) => {
  const ports = it?.item?.ports;
  if (Array.isArray(ports)) return ports.filter((p) => p != null);
  if (ports != null) return [ports];
  return [];
};

const fetchVirtualItems = async (virtualDeviceId) => {
  if (virtualDeviceId == null) return [];
  try {
    const result = await Axios.get(
      VirtualDeviceApi.VIRTUAL_DEVICE.replace(':id', virtualDeviceId),
    );
    if (result?.resultCode !== '0000') return [];
    return normalizeItems(result.resultData?.items);
  } catch (e) {
    console.error('virtualDevice 조회 실패:', virtualDeviceId, e);
    return [];
  }
};

const publishLampCmdC = (mqttControl, it, onoff) => {
  if (!mqttControl || Number(it?.type) !== DeviceType.Lamp) return;
  const controllerId = it.controllerId;
  /* cs 보드(M24/M22/M02): smarthome-api cmd vc 에 맡기고 front c 중복 발행 방지 */
  if (!controllerId || supportsLampCmdCs(controllerId)) return;

  const ports = lampPortsOf(it);
  if (ports.length === 0) return;

  const bright = it.item?.bright != null ? parseInt(it.item.bright, 10) : 0;
  const color = it.item?.color ?? 0;
  mqttControl.onPublish(controllerId, it._id, {
    serial: controllerId,
    cmd: 'c',
    type: 1,
    data: {
      ports,
      onoff,
      bright: Number.isFinite(bright) ? bright : 0,
      color,
    },
    origin: VIRTUAL_BUTTON_MQTT_ORIGIN,
  });
};

/**
 * 구형(cs/vc 미지원) 보드용: 가상버튼 멤버 조명에 개별 cmd "c" 발행.
 * M24/M22/M02는 smarthome-api의 vc에 맡기고 스킵.
 *
 * @param {object} opts
 * @param {object} opts.mqttControl useMqttControl 인스턴스
 * @param {object} [opts.store] vuex store (가상버튼 OFF 동기화 suppress)
 * @param {string|number} opts.virtualDeviceId 조작 대상 가상버튼 id
 * @param {0|1|boolean} opts.turnOn ON이면 스냅샷 onoff, OFF면 0
 * @param {string|number} [opts.prevVirtualDeviceId] A→B 전환 시 이전 ON 가상버튼
 */
export async function publishVirtualButtonLampsCmdC({
  mqttControl,
  store,
  virtualDeviceId,
  turnOn,
  prevVirtualDeviceId,
}) {
  const isOn = turnOn === true || turnOn === 1;
  const items = await fetchVirtualItems(virtualDeviceId);
  if (items.length === 0) return;

  // state/changed(cmd d) 수신 시 가상버튼 UI가 꺼지지 않도록
  if (store?.commit) {
    store.commit('home/suppressVirtualButtonOff', { ms: 3500, onoff: isOn ? 1 : 0 });
  }

  if (isOn && prevVirtualDeviceId != null && String(prevVirtualDeviceId) !== String(virtualDeviceId)) {
    const prevItems = await fetchVirtualItems(prevVirtualDeviceId);
    const nextIdSet = new Set(items.map((it) => String(it?._id)));
    for (const it of prevItems) {
      if (Number(it?.type) !== DeviceType.Lamp) continue;
      if (nextIdSet.has(String(it._id))) continue;
      publishLampCmdC(mqttControl, it, 0);
    }
  }

  for (const it of items) {
    if (Number(it?.type) !== DeviceType.Lamp) continue;
    const onoff = isOn ? (it.item?.onoff ?? 0) : 0;
    publishLampCmdC(mqttControl, it, onoff);
  }
}
